import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSession,
  getSessionCalcData,
  getSessions,
  updateSession,
  deleteSession,
  getTyreReferenceSessions,
  getTyreReport,
  updateWeatherData,
  duplicateSession,
  setWheelAndReprocess,
  assignCrr,
  reorderConfigs,
} from '../api/sessions';
import { Session } from '../types/Session';

export const useSessions = (queryString: string = '') =>
  useQuery({ queryKey: ['sessions', queryString], queryFn: () => getSessions(queryString) });

export const useSessionsWithConfigs = () =>
  useQuery({ queryKey: ['sessions', 'with_configs'], queryFn: () => getSessions('scope=with_configs') });

export const useSessionCalcData = (sessionId: string) =>
  useQuery({
    queryKey: ['session', sessionId],
    queryFn: (): Promise<Session> => getSessionCalcData(sessionId),
    enabled: !!sessionId && sessionId !== 'no-session-selected', // Only fetch if sessionId is valid
  });

export const useUpdateSession = (sessionId: string) =>
  useMutation({ mutationFn: (data: any) => updateSession(sessionId, data) });

export const useCreateSession = () => useMutation({ mutationFn: (data: any) => createSession(data) });

export const useSetSession = () => {
  const queryClient = useQueryClient();
  return (data: any) => {
    queryClient.setQueryData(['session', data.id], data);
  };
};

export const useInvalidateSession = (sessionId: string) => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['session', sessionId] });
  };
};

export const useDeleteSession = () => useMutation({ mutationFn: (sessionId: string) => deleteSession(sessionId) });

export const useTyreReferenceSessions = () =>
  useQuery({ queryKey: ['tyre-reference-sessions'], queryFn: getTyreReferenceSessions });

export const useSessionTyreReference = (sessionId: string) =>
  useQuery({
    queryKey: ['session-tyre-reference', sessionId],
    queryFn: () => getTyreReport(sessionId),
    enabled: !!sessionId,
  });

export const useUpdateWeatherData = (sessionId: string) =>
  useMutation({ mutationFn: () => updateWeatherData(sessionId) });

export const useDuplicateSession = () =>
  useMutation({
    mutationFn: (
      { sessionId, mode }:
      { sessionId: string; mode: 'with_runs' | 'without_runs' },
    ) => duplicateSession(sessionId, mode),
  });

export const useSetWheelAndReprocess = (sessionId: string) =>
  useMutation({
    mutationFn: () => setWheelAndReprocess(sessionId),
  });

// Dynamic variant — session id is supplied per mutate call instead of at hook
// creation. Used by composite views that need to iterate across multiple
// sessions in a single user action (e.g. Tyre Reports Coordination).
export const useSetWheelAndReprocessDynamic = () =>
  useMutation({
    mutationFn: (sessionId: string) => setWheelAndReprocess(sessionId),
  });

export const useAssignCrr = (sessionId: string) =>
  useMutation({
    mutationFn: () => assignCrr(sessionId),
  });

export const useReorderConfigs = (sessionId: string) =>
  useMutation({
    mutationFn: (body: Record<string, any>) =>
      reorderConfigs(sessionId, body),
  });
